import type { BlockedUserItem } from '@/types';

import React from 'react';
import { Button, Chip, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Tooltip, useDisclosure } from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';
import { toast } from 'react-toastify';

import { TablePage, TableFilters, ColumnsToggleDropdown } from '@/components/table';
import { blockedUsersListParamsAtom, blockedUsersQueryAtom, unblockBlockedUser } from '@/store';
import { useBlockedUsersFilters, useBlockedUsersSorting } from '@/hooks/blockedUsers';
import { useVisibleColumns } from '@/hooks/table';
import { SearchFieldsComponent } from '@/components/blockedUsers/SearchFields';
import { FingerprintChip } from '@/components/shared/FingerprintChip';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { DomainCell } from '@/components/shared/DomainCell';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { RiLockUnlockLine } from '@remixicon/react';

export default function BlockedUsersPage() {
  const { data, isLoading, isError, error } = useAtomValue(blockedUsersQueryAtom);
  const [params, setParams] = useAtom(blockedUsersListParamsAtom);

  const blockedUsers = React.useMemo<BlockedUserItem[]>(() => data?.data ?? [], [data]);

  const {
    searchFields,
    setSearchFields,
    isSearchOpen,
    setIsSearchOpen,
    hasAnyFilter,
    applyFilters,
    clearFilters,
    handlePageChange
  } = useBlockedUsersFilters();

  const { sortDescriptor, setSortDescriptor, sortedBlockedUsers } = useBlockedUsersSorting(blockedUsers);

  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  const { isOpen: isUnblockOpen, onOpen: onUnblockOpen, onOpenChange: onUnblockOpenChange } = useDisclosure();
  const [selectedBlockedUser, setSelectedBlockedUser] = React.useState<BlockedUserItem | null>(null);
  const [isUnblocking, setIsUnblocking] = React.useState(false);

  const handleUnblockClick = React.useCallback((item: BlockedUserItem) => {
    setSelectedBlockedUser(item);
    onUnblockOpen();
  }, [onUnblockOpen]);

  const handleUnblockConfirm = React.useCallback(async () => {
    if (!selectedBlockedUser) return;
    try {
      setIsUnblocking(true);
      await unblockBlockedUser(selectedBlockedUser.id);
      toast.success('User unblocked successfully');
      triggerRefresh();
      onUnblockOpenChange();
      setSelectedBlockedUser(null);
    } catch (err) {
      // Error handled by axios interceptor
    } finally {
      setIsUnblocking(false);
    }
  }, [selectedBlockedUser, triggerRefresh, onUnblockOpenChange]);

  const handleUnblockClose = React.useCallback(() => {
    onUnblockOpenChange();
    setSelectedBlockedUser(null);
  }, [onUnblockOpenChange]);

  const renderCell = React.useCallback((item: BlockedUserItem, columnKey: React.Key) => {
    switch (columnKey) {
      case 'id':
        return <span>{item.id}</span>;
      case 'fingerprint':
        return <FingerprintChip fingerprint={item.fingerprint} />;
      case 'domain': {
        const domain = item.domain;
        if (!domain) return <span className="text-default-400">—</span>;
        return (
          <DomainCell
            domain={{
              id: domain.id,
              domain: domain.domain || '—',
              title: domain.title ?? undefined
            }}
            showTitle={Boolean(domain.title)}
            dense
            linked
            showID
          />
        );
      }
      case 'reason':
        return <span>{item.reason ?? '-'}</span>;
      case 'ip':
        return <span>{item.ip ?? '-'}</span>;
      case 'blocked_at':
        return <DateTimeDisplay dateString={item.blocked_at} showSeconds />;
      case 'status': {
        const color = item.is_blocked ? 'danger' : 'success';
        const label = item.is_blocked ? 'Blocked' : 'Unblocked';
        return (
          <Chip color={color} size="sm" variant="flat">
            {label}
          </Chip>
        );
      }
      case 'actions':
        if (!item.is_blocked) return null;
        return (
          <div className="flex justify-end">
            <PermissionGuard permission="BlockedUser.unblock">
              <Tooltip content="Unblock user">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="primary"
                  aria-label="Unblock"
                  onPress={() => handleUnblockClick(item)}
                >
                  <RiLockUnlockLine className="h-4 w-4" />
                </Button>
              </Tooltip>
            </PermissionGuard>
          </div>
        );
      default:
        return null;
    }
  }, [handleUnblockClick]);

  const columns = React.useMemo(
    () => [
      { name: 'ID', uid: 'id', sortable: true, align: 'center' as const },
      { name: 'Fingerprint', uid: 'fingerprint', sortable: true },
      { name: 'Domain', uid: 'domain', sortable: true },
      { name: 'Reason', uid: 'reason', sortable: true },
      { name: 'IP', uid: 'ip', sortable: true },
      { name: 'Blocked At', uid: 'blocked_at', sortable: true, align: 'end' as const, cellClass: 'text-right' },
      { name: 'Status', uid: 'status', sortable: true },
      { name: 'Actions', uid: 'actions', sortable: false, align: 'end' as const, cellClass: 'text-right' }
    ],
    []
  );

  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns, [
    'id',
    'fingerprint',
    'domain',
    'reason',
    'ip',
    'blocked_at',
    'status',
    'actions'
  ]);

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={sortedBlockedUsers}
      emptyMessage="No blocked users found"
      error={error}
      errorMessage="Failed to load blocked users"
      filtersSection={
        <TableFilters
          hasActiveFilters={hasAnyFilter}
          isRefreshing={isLoading}
          onClearFilters={clearFilters}
          onRefresh={triggerRefresh}
        >
          <SearchFieldsComponent
            searchFields={searchFields}
            setSearchFields={setSearchFields}
            isSearchOpen={isSearchOpen}
            setIsSearchOpen={setIsSearchOpen}
            onApply={applyFilters}
            onClear={clearFilters}
          />
          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={(next) => setVisibleColumns(next)}
          />
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={sortDescriptor}
      title="Blocked Users"
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={(d) => setSortDescriptor(d)}
    >
      <Modal isOpen={isUnblockOpen} placement="center" onOpenChange={handleUnblockClose} size="md" isDismissable={false}>
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1">Unblock User</ModalHeader>
              <ModalBody>
                <p>
                  Are you sure you want to unblock this user?
                  {selectedBlockedUser && (
                    <>
                      {' '}
                      Fingerprint: <code className="text-xs">{selectedBlockedUser.fingerprint}</code>
                    </>
                  )}
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={handleUnblockClose}>
                  Cancel
                </Button>
                <Button color="primary" isLoading={isUnblocking} onPress={handleUnblockConfirm}>
                  Unblock
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </TablePage>
  );
}
